/**
 * React Query hooks for OCTOPUS agent configuration API
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
} from '../../../shared/api/axiosInstance';
import {
  getBudgetConfigApiV1AiAgentsAgentsAgentIdBudgetConfigGet,
  getGetBudgetConfigApiV1AiAgentsAgentsAgentIdBudgetConfigGetQueryKey,
  updateBudgetConfigApiV1AiAgentsAgentsAgentIdBudgetConfigPut,
} from '../../../generated/api/ai-agents-processing/ai-agents-processing';
import type {
  DataResponse,
  PendingApproval,
  AgentObjective,
  AgentTool,
  GroupedTools,
  McpConnection,
  ModelConfig,
  ProcessingStats,
  BudgetConfigUpdate,
  TrackedPerson,
  ConnectedAgent,
  NotificationChannel,
  ScheduledTask,
  ScheduledTaskCreate,
  ScheduledTaskUpdate,
  ScheduleRun,
  EventTrigger,
  EventTriggerCreate,
  EventTriggerUpdate,
  TriggerExecution,
  EventTypeInfo,
  ActionCatalogItem,
  ActionTemplate,
  RecentActivity,
  OctopusDashboardData,
  AgentUserRelationship,
  AgentUserRelationshipCreate,
  AgentUserRelationshipUpdate,
  UserAccordionData,
  ActivityHistoryResponse,
  ActivityHistoryFilters,
  ActivityDetail,
  ActivityType,
  AgentToolCallsResponse,
} from '../types';

const API_BASE = '/api/v1/ai-agents';

// Query keys
export const OCTOPUS_KEYS = {
  dashboard: (agentId: string) => ['octopus', 'dashboard', agentId] as const,
  pendingApprovals: (agentId: string) => ['octopus', 'pending-approvals', agentId] as const,
  inboxApprovals: () => ['octopus', 'inbox-approvals'] as const,
  inboxApprovalsCount: () => ['octopus', 'inbox-approvals-count'] as const,
  approvalHistory: (agentId: string) => ['octopus', 'approval-history', agentId] as const,
  recentApprovals: (agentId: string) => ['octopus', 'recent-approvals', agentId] as const,
  objectives: (agentId: string) => ['octopus', 'objectives', agentId] as const,
  tools: (agentId: string) => ['octopus', 'tools', agentId] as const,
  toolsSettings: (agentId: string) => ['octopus', 'tools-settings', agentId] as const,
  mcpConnections: (agentId: string) => ['octopus', 'mcp-connections', agentId] as const,
  modelConfig: (agentId: string) => ['octopus', 'model-config', agentId] as const,
  processingStats: (agentId: string) => ['octopus', 'processing-stats', agentId] as const,
  trackedPeople: (agentId: string) => ['octopus', 'tracked-people', agentId] as const,
  connectedAgents: (agentId: string) => ['octopus', 'connected-agents', agentId] as const,
  notificationChannels: (agentId: string) => ['octopus', 'notification-channels', agentId] as const,
  relationships: (agentId: string) => ['octopus', 'relationships', agentId] as const,
  manager: (agentId: string) => ['octopus', 'manager', agentId] as const,
  scheduledTasks: (agentId: string) => ['octopus', 'scheduled-tasks', agentId] as const,
  eventTriggers: (agentId: string) => ['octopus', 'event-triggers', agentId] as const,
  recentActivity: (agentId: string) => ['octopus', 'recent-activity', agentId] as const,
  activityHistory: (agentId: string, filters?: Record<string, unknown>) =>
    ['octopus', 'activity-history', agentId, filters] as const,
  activityDetail: (agentId: string, activityId: string, type: string) =>
    ['octopus', 'activity-detail', agentId, activityId, type] as const,
  actionTemplates: () => ['octopus', 'action-templates'] as const,
  toolCalls: (agentId: string, filters?: Record<string, unknown>) =>
    ['octopus', 'tool-calls', agentId, filters] as const,
};

// ============================================================================
// Dashboard
// ============================================================================

export function useOctopusDashboard(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.dashboard(agentId),
    queryFn: async () => {
      const response = await apiGet<DataResponse<OctopusDashboardData>>(
        `${API_BASE}/agents/${agentId}/octopus/dashboard`
      );
      return response.data;
    },
    enabled: !!agentId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

// ============================================================================
// Pending Approvals
// ============================================================================

/**
 * Pending approvals for ONE agent — the Supervision-tab / overview queue.
 * Scoped server-side to `agent_id`: the caller must have view access to the
 * agent (approver rights or ReBAC agent access). We no longer fetch the whole
 * tenant's approvals and client-filter — that leaked every agent's approval
 * payloads to any authenticated user.
 */
export function usePendingApprovals(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.pendingApprovals(agentId),
    queryFn: async () => {
      const response = await apiGet<DataResponse<PendingApproval[]>>(
        `${API_BASE}/approvals?agent_id=${agentId}&status=pending`
      );
      return response.data;
    },
    enabled: !!agentId,
    refetchInterval: 30 * 1000, // Poll every 30 seconds
  });
}

/**
 * Tenant-wide approval inbox — every OPEN approval THIS user may resolve, across
 * all agents (admins see all). Authorization is enforced server-side; this is the
 * cross-agent queue behind the "Approvals" nav item.
 */
export function useInboxApprovals() {
  return useQuery({
    queryKey: OCTOPUS_KEYS.inboxApprovals(),
    queryFn: async () => {
      const response = await apiGet<{
        data: PendingApproval[];
        pagination: { limit: number; offset: number; total: number };
      }>(`${API_BASE}/approvals/inbox`);
      return response.data;
    },
    refetchInterval: 30 * 1000,
  });
}

/**
 * Cheap count of open approvals the user may resolve — drives the sidebar badge.
 * Lives in the always-mounted sidebar, so it hits a count-only endpoint rather
 * than loading the full inbox.
 */
export function useInboxApprovalsCount() {
  return useQuery({
    queryKey: OCTOPUS_KEYS.inboxApprovalsCount(),
    queryFn: async () => {
      const response = await apiGet<{ count: number }>(`${API_BASE}/approvals/inbox/count`);
      return response.count;
    },
    refetchInterval: 30 * 1000,
  });
}

export function useApprovalHistory(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.approvalHistory(agentId),
    queryFn: async () => {
      const response = await apiGet<{
        data: PendingApproval[];
        pagination: { limit: number; offset: number; total: number };
      }>(
        `${API_BASE}/approvals?agent_id=${agentId}&status=approved,always_approved,rejected,edited&limit=20`
      );
      return response.data;
    },
    enabled: !!agentId,
  });
}

export function useRecentApprovals(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.recentApprovals(agentId),
    queryFn: async () => {
      const response = await apiGet<{
        data: PendingApproval[];
        pagination: { limit: number; offset: number; total: number };
      }>(
        `${API_BASE}/approvals?agent_id=${agentId}&status=pending,approved,always_approved,rejected,edited,expired&limit=8`
      );
      return response;
    },
    enabled: !!agentId,
  });
}

export function useApprovalHistoryFull(agentId: string, limit = 50, offset = 0) {
  return useQuery({
    queryKey: [...OCTOPUS_KEYS.approvalHistory(agentId), 'full', limit, offset] as const,
    queryFn: async () => {
      const response = await apiGet<{
        data: PendingApproval[];
        pagination: { limit: number; offset: number; total: number };
      }>(
        `${API_BASE}/approvals?agent_id=${agentId}&status=pending,approved,always_approved,rejected,edited,expired&limit=${limit}&offset=${offset}`
      );
      return response;
    },
    enabled: !!agentId,
  });
}

// ============================================================================
// Objectives & Metrics
// ============================================================================

export function useAgentObjectives(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.objectives(agentId),
    queryFn: async () => {
      const response = await apiGet<DataResponse<AgentObjective | null>>(
        `${API_BASE}/agents/${agentId}/objectives`
      );
      return response.data;
    },
    enabled: !!agentId,
  });
}

export function useCreateObjective() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      objective,
    }: {
      agentId: string;
      objective: Partial<AgentObjective>;
    }) => {
      const response = await apiPost<DataResponse<AgentObjective>>(
        `${API_BASE}/agents/${agentId}/objectives`,
        objective
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.objectives(variables.agentId),
      });
    },
  });
}

/**
 * Mutation hook for updating objectives with inline edit pattern.
 * Uses POST as upsert - creates if not exists, updates if exists.
 */
export function useSetObjectives() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      goalDescription,
      successCriteria,
      scopeExclusions,
      boundaryRules,
    }: {
      agentId: string;
      goalDescription: string;
      successCriteria?: Array<{ metric: string; target: number; comparison: string; weight?: number }>;
      scopeExclusions?: string[];
      boundaryRules?: string[];
    }) => {
      const response = await apiPost<DataResponse<AgentObjective>>(
        `${API_BASE}/agents/${agentId}/objectives`,
        {
          goal_description: goalDescription,
          success_criteria: successCriteria,
          scope_exclusions: scopeExclusions,
          boundary_rules: boundaryRules,
        }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.objectives(variables.agentId),
      });
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.dashboard(variables.agentId),
      });
    },
  });
}

// ============================================================================
// OKR Board
// ============================================================================

export function useCreateOkrBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentId }: { agentId: string }) => {
      const response = await apiPost<DataResponse<{ board_id: string; board_name: string }>>(
        `${API_BASE}/agents/${agentId}/objectives/okr-board`,
        {}
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      toast.success(data.board_name ?
        `OKR board "${data.board_name}" created` : 'OKR board created');
      // Invalidate agent data to pick up okr_board_id
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      queryClient.invalidateQueries({ queryKey: ['ai-agents-agent', variables.agentId] });
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.objectives(variables.agentId),
      });
    },
    // The global mutation-error handler shows the specific translated toast.
  });
}

export function useUpdateOkrBoardAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      writeAccess,
    }: {
      agentId: string;
      writeAccess: boolean;
    }) => {
      const response = await apiPut<DataResponse<{ write_access: boolean }>>(
        `${API_BASE}/agents/${agentId}/objectives/okr-board/access`,
        { write_access: writeAccess }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      queryClient.invalidateQueries({ queryKey: ['ai-agents-agent', variables.agentId] });
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.objectives(variables.agentId),
      });
    },
  });
}

// ============================================================================
// Tools
// ============================================================================

export function useAgentTools(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.tools(agentId),
    queryFn: async () => {
      const response = await apiGet<DataResponse<AgentTool[]>>(
        `${API_BASE}/agents/${agentId}/tools`
      );
      return response.data;
    },
    enabled: !!agentId,
  });
}

export function useGroupedTools(agentId: string) {
  return useQuery({
    queryKey: [...OCTOPUS_KEYS.tools(agentId), 'grouped'] as const,
    queryFn: async () => {
      const response = await apiGet<DataResponse<GroupedTools>>(
        `${API_BASE}/agents/${agentId}/tools/grouped`
      );
      return response.data;
    },
    enabled: !!agentId,
  });
}

export function useToggleTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      toolId,
      enabled,
    }: {
      agentId: string;
      toolId: string;
      enabled: boolean;
    }) => {
      const response = await apiPut<DataResponse<AgentTool>>(
        `${API_BASE}/agents/${agentId}/tools/${toolId}`,
        { is_enabled: enabled }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.tools(variables.agentId),
      });
    },
  });
}

export function useToolsSettings(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.toolsSettings(agentId),
    queryFn: async () => {
      const response = await apiGet<DataResponse<ToolsSettings>>(
        `${API_BASE}/agents/${agentId}/tools-settings`
      );
      return response.data;
    },
    enabled: !!agentId,
  });
}

export function useUpdateToolsSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      data,
    }: {
      agentId: string;
      data: Partial<ToolsSettings>;
    }) => {
      const response = await apiPut<DataResponse<ToolsSettings>>(
        `${API_BASE}/agents/${agentId}/tools-settings`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.toolsSettings(variables.agentId),
      });
    },
  });
}

export interface ToolsSettings {
  web_search_enabled: boolean;
  web_fetch_enabled: boolean;
  web_navigate_enabled: boolean;
  responds_to_messaging: boolean;
  chattiness_mode: 'silent' | 'mentions_only' | 'proactive';
  self_config: {
    objectives: 'never' | 'with_approval' | 'allowed';
    schedule: 'never' | 'with_approval' | 'allowed';
  };
  spawning: {
    tier: 'never' | 'with_approval' | 'allowed';
    inherit_tools: boolean;
    inherit_shared_data: boolean;
    max_descendants: number;
  };
  oversight: {
    escalate_after_minutes: number | null;
    escalation_target_user_id: string | null;
    approver_group_id: string | null;
    approval_timeout_hours: number | null;
  };
}

export function useMcpConnections(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.mcpConnections(agentId),
    queryFn: async () => {
      const response = await apiGet<DataResponse<McpConnection[]>>(
        `${API_BASE}/agents/${agentId}/mcp-connections`
      );
      return response.data;
    },
    enabled: !!agentId,
  });
}

// ============================================================================
// Processing/Model Config
// ============================================================================

export function useModelConfig(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.modelConfig(agentId),
    queryFn: async () => {
      const response = await apiGet<DataResponse<ModelConfig>>(
        `${API_BASE}/agents/${agentId}/model-config`
      );
      return response.data;
    },
    enabled: !!agentId,
  });
}

export function useUpdateModelConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      config,
    }: {
      agentId: string;
      config: Partial<ModelConfig>;
    }) => {
      const response = await apiPut<DataResponse<ModelConfig>>(
        `${API_BASE}/agents/${agentId}/model-config`,
        config
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.modelConfig(variables.agentId),
      });
    },
  });
}

export function useProcessingStats(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.processingStats(agentId),
    queryFn: async () => {
      const response = await apiGet<DataResponse<ProcessingStats>>(
        `${API_BASE}/agents/${agentId}/processing-stats`
      );
      return response.data;
    },
    enabled: !!agentId,
    staleTime: 60 * 1000, // 1 minute
  });
}

// ============================================================================
// Budget (spend caps + circuit breakers)
// ============================================================================

// Schema-first: the generated orval client (response_model=AgentBudgetConfig,
// request body BudgetConfigRequest) is the single source of the budget contract.
export function useBudgetConfig(agentId: string) {
  return useQuery({
    queryKey:
      getGetBudgetConfigApiV1AiAgentsAgentsAgentIdBudgetConfigGetQueryKey(agentId),
    queryFn: ({ signal }) =>
      getBudgetConfigApiV1AiAgentsAgentsAgentIdBudgetConfigGet(agentId, signal),
    enabled: !!agentId,
    staleTime: 60 * 1000, // 1 minute
  });
}

export function useUpdateBudgetConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agentId,
      config,
    }: {
      agentId: string;
      config: BudgetConfigUpdate;
    }) =>
      updateBudgetConfigApiV1AiAgentsAgentsAgentIdBudgetConfigPut(agentId, config),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey:
          getGetBudgetConfigApiV1AiAgentsAgentsAgentIdBudgetConfigGetQueryKey(
            variables.agentId,
          ),
      });
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.processingStats(variables.agentId),
      });
    },
  });
}

// ============================================================================
// Users
// ============================================================================

export function useTrackedPeople(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.trackedPeople(agentId),
    queryFn: async () => {
      const response = await apiGet<DataResponse<TrackedPerson[]>>(
        `${API_BASE}/agents/${agentId}/tracked-people`
      );
      return response.data;
    },
    enabled: !!agentId,
  });
}

export function useConnectedAgents(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.connectedAgents(agentId),
    queryFn: async () => {
      const response = await apiGet<DataResponse<ConnectedAgent[]>>(
        `${API_BASE}/agents/${agentId}/connected-agents`
      );
      return response.data;
    },
    enabled: !!agentId,
  });
}

export function useNotificationChannels(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.notificationChannels(agentId),
    queryFn: async () => {
      const response = await apiGet<DataResponse<NotificationChannel[]>>(
        `${API_BASE}/agents/${agentId}/notification-channels`
      );
      return response.data;
    },
    enabled: !!agentId,
  });
}

export function useToggleNotificationChannel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      channelId,
      enabled,
    }: {
      agentId: string;
      channelId: string;
      enabled: boolean;
    }) => {
      const response = await apiPut<DataResponse<NotificationChannel>>(
        `${API_BASE}/agents/${agentId}/notification-channels/${channelId}`,
        { is_enabled: enabled }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.notificationChannels(variables.agentId),
      });
    },
  });
}

// ============================================================================
// Schedule
// ============================================================================

export function useScheduledTasks(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.scheduledTasks(agentId),
    queryFn: async () => {
      const response = await apiGet<{ data: ScheduledTask[]; total: number }>(
        `${API_BASE}/agents/${agentId}/schedules`
      );
      return response.data;
    },
    enabled: !!agentId,
  });
}

export function useCreateSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      data,
    }: {
      agentId: string;
      data: ScheduledTaskCreate;
    }) => {
      const response = await apiPost<DataResponse<ScheduledTask>>(
        `${API_BASE}/agents/${agentId}/schedules`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: OCTOPUS_KEYS.scheduledTasks(agentId) });
    },
  });
}

export function useUpdateSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      scheduleId,
      data,
    }: {
      agentId: string;
      scheduleId: string;
      data: ScheduledTaskUpdate;
    }) => {
      const response = await apiPut<DataResponse<ScheduledTask>>(
        `${API_BASE}/agents/${agentId}/schedules/${scheduleId}`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: OCTOPUS_KEYS.scheduledTasks(agentId) });
    },
  });
}

export function useDeleteSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      scheduleId,
    }: {
      agentId: string;
      scheduleId: string;
    }) => {
      await apiDelete(`${API_BASE}/agents/${agentId}/schedules/${scheduleId}`);
    },
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: OCTOPUS_KEYS.scheduledTasks(agentId) });
    },
  });
}

export function useTriggerScheduleRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      scheduleId,
    }: {
      agentId: string;
      scheduleId: string;
    }) => {
      const response = await apiPost<DataResponse<{ run_id: string; status: string }>>(
        `${API_BASE}/agents/${agentId}/schedules/${scheduleId}/run`,
        {}
      );
      return response.data;
    },
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: OCTOPUS_KEYS.scheduledTasks(agentId) });
      queryClient.invalidateQueries({ queryKey: OCTOPUS_KEYS.recentActivity(agentId) });
    },
  });
}

export function useTriggerScheduleRunInteractive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      scheduleId,
    }: {
      agentId: string;
      scheduleId: string;
    }) => {
      const response = await apiPost<DataResponse<{
        run_id: string;
        chat_id: string;
        schedule_name: string;
        agent_name: string;
      }>>(
        `${API_BASE}/agents/${agentId}/schedules/${scheduleId}/run-interactive`,
        {}
      );
      return response.data;
    },
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: OCTOPUS_KEYS.scheduledTasks(agentId) });
      queryClient.invalidateQueries({ queryKey: OCTOPUS_KEYS.recentActivity(agentId) });
    },
  });
}

export function useCompleteScheduleRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      runId,
      status,
      errorMessage,
      chatId,
    }: {
      agentId: string;
      runId: string;
      status: 'completed' | 'failed';
      errorMessage?: string;
      chatId?: string;
    }) => {
      return apiPost(`${API_BASE}/agents/${agentId}/schedules/runs/${runId}/complete`, {
        status,
        error_message: errorMessage,
        chat_id: chatId,
      });
    },
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: OCTOPUS_KEYS.scheduledTasks(agentId) });
      queryClient.invalidateQueries({ queryKey: OCTOPUS_KEYS.recentActivity(agentId) });
    },
  });
}

export function useStopAutomationRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      runId,
    }: {
      agentId: string;
      runId: string;
    }) => {
      return apiPost(`${API_BASE}/agents/${agentId}/automations/runs/${runId}/stop`, {});
    },
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: OCTOPUS_KEYS.scheduledTasks(agentId) });
      queryClient.invalidateQueries({ queryKey: OCTOPUS_KEYS.recentActivity(agentId) });
    },
  });
}

export function useScheduleRuns(agentId: string, scheduleId?: string) {
  return useQuery({
    queryKey: [...OCTOPUS_KEYS.scheduledTasks(agentId), 'runs', scheduleId],
    queryFn: async () => {
      const url = scheduleId
        ? `${API_BASE}/agents/${agentId}/schedules/${scheduleId}/runs`
        : `${API_BASE}/agents/${agentId}/schedules/runs`;
      const response = await apiGet<DataResponse<ScheduleRun[]>>(url);
      return response.data;
    },
    enabled: !!agentId,
  });
}

export function useEventTriggers(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.eventTriggers(agentId),
    queryFn: async () => {
      const response = await apiGet<{ data: EventTrigger[]; total: number }>(
        `${API_BASE}/agents/${agentId}/triggers`
      );
      return response.data;
    },
    enabled: !!agentId,
  });
}

export function useCreateEventTrigger() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      data,
    }: {
      agentId: string;
      data: EventTriggerCreate;
    }) => {
      const response = await apiPost<DataResponse<EventTrigger>>(
        `${API_BASE}/agents/${agentId}/triggers`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: OCTOPUS_KEYS.eventTriggers(agentId) });
    },
  });
}

export function useUpdateEventTrigger() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      triggerId,
      data,
    }: {
      agentId: string;
      triggerId: string;
      data: EventTriggerUpdate;
    }) => {
      const response = await apiPut<DataResponse<EventTrigger>>(
        `${API_BASE}/agents/${agentId}/triggers/${triggerId}`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: OCTOPUS_KEYS.eventTriggers(agentId) });
    },
  });
}

export function useDeleteEventTrigger() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      triggerId,
    }: {
      agentId: string;
      triggerId: string;
    }) => {
      await apiDelete(`${API_BASE}/agents/${agentId}/triggers/${triggerId}`);
    },
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: OCTOPUS_KEYS.eventTriggers(agentId) });
    },
  });
}

export function useTriggerExecutions(agentId: string, triggerId?: string) {
  return useQuery({
    queryKey: [...OCTOPUS_KEYS.eventTriggers(agentId), 'executions', triggerId],
    queryFn: async () => {
      const url = triggerId
        ? `${API_BASE}/agents/${agentId}/triggers/${triggerId}/executions`
        : `${API_BASE}/agents/${agentId}/triggers/executions/all`;
      const response = await apiGet<{ data: TriggerExecution[]; total: number }>(url);
      return response.data;
    },
    enabled: !!agentId,
  });
}

export function useEventTypes() {
  return useQuery({
    queryKey: ['octopus', 'event-types'],
    queryFn: async () => {
      const response = await apiGet<{
        event_types: EventTypeInfo[];
        action_catalog: ActionCatalogItem[];
      }>(`${API_BASE}/event-types`);
      return response;
    },
  });
}

export function useActionTemplates() {
  return useQuery<ActionTemplate[]>({
    queryKey: OCTOPUS_KEYS.actionTemplates(),
    queryFn: async () => {
      const response = await apiGet<{ data?: ActionTemplate[] }>(
        `${API_BASE}/action-templates`
      );
      if (response.data && Array.isArray(response.data)) {
        return response.data;
      }
      console.warn('Unexpected action-templates response shape:', response);
      return [];
    },
  });
}

export function useRecentActivity(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.recentActivity(agentId),
    queryFn: async () => {
      const response = await apiGet<DataResponse<RecentActivity[]>>(
        `${API_BASE}/agents/${agentId}/recent-activity`
      );
      return response.data;
    },
    enabled: !!agentId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useToggleAgentEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ agentId, isActive }: { agentId: string; isActive: boolean }) => {
      const response = await apiPut<{ email_address: string; email_enabled: boolean }>(
        `${API_BASE}/agents/${agentId}/email-settings`,
        { is_active: isActive }
      );
      return response;
    },
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: ['ai-agents-agent', agentId] });
    },
  });
}

export function useActivityHistory(
  agentId: string,
  filters: ActivityHistoryFilters = {},
  pagination: { limit: number; offset: number } = { limit: 25, offset: 0 }
) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.activityHistory(agentId, { ...filters, ...pagination }),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('limit', String(pagination.limit));
      params.set('offset', String(pagination.offset));
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      if (filters.status) params.set('status', filters.status);
      if (filters.schedule_id) params.set('schedule_id', filters.schedule_id);
      if (filters.search) params.set('search', filters.search);

      const response = await apiGet<ActivityHistoryResponse>(
        `${API_BASE}/agents/${agentId}/activity-history?${params.toString()}`
      );
      return response;
    },
    enabled: !!agentId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useActivityDetail(
  agentId: string,
  activityId: string,
  activityType: ActivityType
) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.activityDetail(agentId, activityId, activityType),
    queryFn: async () => {
      const response = await apiGet<{ data: ActivityDetail }>(
        `${API_BASE}/agents/${agentId}/activity-history/${activityId}?activity_type=${activityType}`
      );
      return response.data;
    },
    enabled: !!agentId && !!activityId,
  });
}

// ============================================================================
// Tool Call History
// ============================================================================

export interface ToolCallFilters {
  status?: string;
  module?: string;
  search?: string;
}

export function useAgentToolCalls(
  agentId: string,
  filters: ToolCallFilters = {},
  pagination: { limit: number; offset: number } = { limit: 25, offset: 0 },
  sort: { sortBy: string; sortDirection: 'asc' | 'desc' } = { sortBy: 'created_at', sortDirection: 'desc' }
) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.toolCalls(agentId, { ...filters, ...pagination, ...sort }),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('limit', String(pagination.limit));
      params.set('offset', String(pagination.offset));
      if (filters.status) params.set('status', filters.status);
      if (filters.module) params.set('module', filters.module);
      if (filters.search) params.set('search', filters.search);
      params.set('sort_by', sort.sortBy);
      params.set('sort_direction', sort.sortDirection);

      const response = await apiGet<AgentToolCallsResponse>(
        `${API_BASE}/agents/${agentId}/tool-calls?${params.toString()}`
      );
      return response;
    },
    enabled: !!agentId,
    staleTime: 30 * 1000,
  });
}

// ============================================================================
// User Relationships
// ============================================================================

export function useAgentRelationships(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.relationships(agentId),
    queryFn: async () => {
      const response = await apiGet<DataResponse<AgentUserRelationship[]>>(
        `${API_BASE}/agents/${agentId}/relationships`
      );
      return response.data;
    },
    enabled: !!agentId,
  });
}

export function useAgentManager(agentId: string) {
  return useQuery({
    queryKey: OCTOPUS_KEYS.manager(agentId),
    queryFn: async () => {
      const response = await apiGet<DataResponse<AgentUserRelationship | null>>(
        `${API_BASE}/agents/${agentId}/manager`
      );
      return response.data;
    },
    enabled: !!agentId,
  });
}

export function useCreateRelationship() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      relationship,
    }: {
      agentId: string;
      relationship: AgentUserRelationshipCreate;
    }) => {
      const response = await apiPost<DataResponse<AgentUserRelationship>>(
        `${API_BASE}/agents/${agentId}/relationships`,
        relationship
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.relationships(variables.agentId),
      });
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.manager(variables.agentId),
      });
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.connectedAgents(variables.agentId),
      });
    },
  });
}

export function useDeleteRelationship() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      relationshipId,
    }: {
      agentId: string;
      relationshipId: string;
    }) => {
      await apiDelete(
        `${API_BASE}/agents/${agentId}/relationships/${relationshipId}`
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.relationships(variables.agentId),
      });
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.manager(variables.agentId),
      });
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.connectedAgents(variables.agentId),
      });
    },
  });
}

export function useUpdateRelationship() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      relationshipId,
      data,
    }: {
      agentId: string;
      relationshipId: string;
      data: AgentUserRelationshipUpdate;
    }) => {
      const response = await apiPut<DataResponse<AgentUserRelationship>>(
        `${API_BASE}/agents/${agentId}/relationships/${relationshipId}`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.relationships(variables.agentId),
      });
    },
  });
}

export function useUserAccordionData(agentId: string, relationshipId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...OCTOPUS_KEYS.relationships(agentId), relationshipId, 'user-data'],
    queryFn: async () => {
      const response = await apiGet<DataResponse<UserAccordionData>>(
        `${API_BASE}/agents/${agentId}/relationships/${relationshipId}/user-data`
      );
      return response.data;
    },
    enabled: !!agentId && !!relationshipId && enabled,
  });
}

export function useChangeManager() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      userId,
    }: {
      agentId: string;
      userId: string;
    }) => {
      const response = await apiPut<DataResponse<AgentUserRelationship>>(
        `${API_BASE}/agents/${agentId}/manager`,
        { user_id: userId }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.relationships(variables.agentId),
      });
      queryClient.invalidateQueries({
        queryKey: OCTOPUS_KEYS.manager(variables.agentId),
      });
    },
  });
}

